import { useLayoutEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

import Header from '@/components/Layout/Header';
import Sidebar from '@/components/Sidebar';
import { RootState } from '@/utils/store';

import FullScreenDisabler from '../FullScreenDisabler';
import ScrollToTopButton from '../ScrollToTopButton';
import StickyButtons from '../StickyButtons';
import ToastDialogs from '../ToastDialogs';
import VideoGuideModal from '../VideoGuideModal';
import Footer from './Footer';

const Wrap = styled.div`
  display: flex;
  height: 100vh;
  min-width: 1440px;
  overflow-x: auto;
  ${({ theme }) => theme.breakpoints.mediumDesktop} {
    min-width: 800px;
  }
`;

const Main = styled.main`
  background: ${({ theme }) => theme.colors.grayscaleLight};
  flex: 1;
  padding: 26px 24px 0;
  min-height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

export const Container = styled.div<{ $fullScreenModeEnabled?: boolean }>`
  width: 1120px;
  max-width: 100%;
  margin: 0 auto;
  min-height: calc(100% - 160px);
  position: relative;
  padding-bottom: 50px;
  ${({ $fullScreenModeEnabled }) =>
    $fullScreenModeEnabled &&
    css`
      width: 100%;
    `}
  ${({ theme }) => theme.breakpoints.mediumDesktop} {
    width: 100%;
  }
`;

const Layout = () => {
  const fullScreenModeEnabled = useSelector(
    (rootState: RootState) => rootState.layout.fullScreenModeEnabled,
  );

  const location = useLocation();

  const ref = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    ref.current?.scrollTo(0, 0);
  }, [location.pathname]);

  const fullWidthPaths = [
    '/freelancers/request-vacation',
    '/freelancers/invoices',
    '/freelancers/invoices/generate',
  ];

  const isFullWidthPage = fullWidthPaths.some((path) =>
    location.pathname.endsWith(path),
  );

  const hideSidebarAndHeader = fullScreenModeEnabled || isFullWidthPage;
  const isFluidContainer = fullScreenModeEnabled || isFullWidthPage;

  return (
    <Wrap>
      {!hideSidebarAndHeader && <Sidebar />}
      <Main id="dashboard-scrollable-container" ref={ref}>
        {!hideSidebarAndHeader && <Header />}
        <Container $fullScreenModeEnabled={isFluidContainer}>
          <Outlet />
        </Container>
        <Footer />
      </Main>
      <VideoGuideModal />
      <StickyButtons>
        <ToastDialogs />
        <ScrollToTopButton />
        <FullScreenDisabler />
      </StickyButtons>
    </Wrap>
  );
};

export default Layout;
